'use client'

import { useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import {
  Avatar,
  Button,
  EntityListShell,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDateTime,
  toast,
} from '@rr/ui'

type AuditEvent = {
  id: string
  ts: string
  actor: { name: string; email: string; type: 'platform' | 'company' | 'system' }
  action: string
  category: 'auth' | 'companies' | 'users' | 'roles' | 'finance' | 'gps' | 'system'
  resource: string
  ip: string
  status: 'success' | 'rejected'
}

const MOCK: AuditEvent[] = [
  { id: 'e1', ts: '2026-04-26T11:42:18Z', actor: { name: 'IKAY', email: 'ikaysapps@gmail.com', type: 'platform' }, action: 'role.update', category: 'roles', resource: 'Platform Admin', ip: '78.84.12.4',  status: 'success' },
  { id: 'e2', ts: '2026-04-26T11:18:02Z', actor: { name: 'Aigerim Bekova', email: 'aigerim@routenroute.com', type: 'platform' }, action: 'company.deactivate', category: 'companies', resource: 'Tbilisi Express', ip: '5.187.3.110', status: 'success' },
  { id: 'e3', ts: '2026-04-26T10:55:39Z', actor: { name: 'Berdy Atayev', email: 'berdy@caspian-imp.com', type: 'company' }, action: 'auth.login', category: 'auth', resource: '—', ip: '37.99.41.21', status: 'success' },
  { id: 'e4', ts: '2026-04-26T10:31:11Z', actor: { name: 'system', email: '—', type: 'system' }, action: 'integration.places.refresh', category: 'system', resource: 'Google Places key', ip: '—', status: 'success' },
  { id: 'e5', ts: '2026-04-26T09:48:50Z', actor: { name: 'Murat Orazov', email: 'murat@asgcargo.tm', type: 'company' }, action: 'auth.login', category: 'auth', resource: '—', ip: '95.85.123.4', status: 'rejected' },
  { id: 'e6', ts: '2026-04-26T09:14:02Z', actor: { name: 'Aigerim Bekova', email: 'aigerim@routenroute.com', type: 'platform' }, action: 'user.impersonate', category: 'users', resource: 'Dilnoza Karimova', ip: '5.187.3.110', status: 'success' },
  { id: 'e7', ts: '2026-04-26T08:02:27Z', actor: { name: 'Tinatin Beridze', email: 'tina@tbilisi-x.ge', type: 'company' }, action: 'invoice.send', category: 'finance', resource: 'INV-2814', ip: '83.220.76.4', status: 'success' },
  { id: 'e8', ts: '2026-04-25T22:11:48Z', actor: { name: 'system', email: '—', type: 'system' }, action: 'gps.threshold.exceeded', category: 'gps', resource: 'Tracker IMEI 86…2371', ip: '—', status: 'success' },
]

const PAGE_SIZE = 10

function fmtTs(iso: string) {
  return formatDateTime(iso)
}

export default function AuditLogSection({ companyId: _companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<'all' | AuditEvent['category']>('all')
  const [range, setRange] = useState<'24h' | '7d' | '30d' | 'all'>('7d')
  const [page, setPage] = useState(1)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const now = Date.now()
    const ranges: Record<typeof range, number> = {
      '24h': 24 * 3600 * 1000,
      '7d':  7 * 24 * 3600 * 1000,
      '30d': 30 * 24 * 3600 * 1000,
      'all': Infinity,
    }
    const cutoff = ranges[range]
    return MOCK.filter((e) => {
      if (q && !`${e.actor.name} ${e.actor.email} ${e.action} ${e.resource}`.toLowerCase().includes(q)) return false
      if (category !== 'all' && e.category !== category) return false
      if (now - new Date(e.ts).getTime() > cutoff) return false
      return true
    })
  }, [search, category, range])

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        subtitle="Every privileged action on this organization — read-only."
        actions={
          <Button variant="outline" onClick={() => toast('CSV export queued (mock)', 'info')}>
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        }
      />

      <EntityListShell
        toolbar={
          <>
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1) }}
              placeholder="Search actor, action, resource…"
              className="max-w-sm"
            />
            <Select value={category} onValueChange={(v) => { setCategory(v as typeof category); setPage(1) }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="auth">Auth</SelectItem>
                <SelectItem value="companies">Companies</SelectItem>
                <SelectItem value="users">Users</SelectItem>
                <SelectItem value="roles">Roles</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="gps">GPS</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
            <Select value={range} onValueChange={(v) => { setRange(v as typeof range); setPage(1) }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Last 24 hours</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        empty={paged.length === 0}
        emptyState="No events match your filters."
        pagination={
          <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onChange={(p) => setPage(p)} />
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="text-sm text-muted-foreground tnum whitespace-nowrap">{fmtTs(e.ts)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar
                      name={e.actor.name}
                      size={28}
                      tone={e.actor.type === 'system' ? 'slate' : e.actor.type === 'platform' ? 'violet' : 'blue'}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{e.actor.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{e.actor.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">{e.action}</code>
                </TableCell>
                <TableCell className="text-sm">{e.resource}</TableCell>
                <TableCell className="text-sm text-muted-foreground tnum">{e.ip}</TableCell>
                <TableCell>
                  <StatusChip status={e.status === 'success' ? 'completed' : 'rejected'} size="sm" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </EntityListShell>
    </div>
  )
}
